import React, { useState, useEffect, useRef } from "react";

export const requestIDMRake = (
  site,
  geoRegion,
  vertical,
  journeyId,
  duid,
  score,
  model,
) => {
  fetch("http://localhost:1415/dev_tools/trigger_prediction", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      site,
      geoRegion,
      vertical,
      journeyId,
      duid,
      score,
      model,
    }),
  })
    .then((response) => response.json())
    .then((data) => console.log("Rake Task Done", data))
    .catch((error) => console.error("Error:", error));
};

/**
 * DevTools component - React implementation of the Ruby haml template
 * @param {Object} props - Component props including site data
 */
function DevTools(props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [showPleaseWait, setShowPleaseWait] = useState(false);
  const devToolsRef = useRef(null);
  const toggleRef = useRef(null);

  // Keep the exact prop names as provided
  const {
    mainSites = [],
    ukSites = [],
    usSites = [],
    verticals = {},
    domainUserId,
    currentSite = "",
    journeyId,
    amplitudeExperiments,
  } = props;

  let { verticalForIdmRequest } = props;
  verticalForIdmRequest = verticalForIdmRequest.toLowerCase();

  // Dismiss tools when clicked outside
  useEffect(() => {
    const handleOutSideClick = (event) => {
      if (!toggleRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscapeClick = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("mousedown", handleOutSideClick);
    window.addEventListener("keydown", handleEscapeClick);

    return () => {
      window.removeEventListener("mousedown", handleOutSideClick);
      window.removeEventListener("keydown", handleEscapeClick);
    };
  }, [toggleRef]);

  // Toggle the dev tools panel
  const toggleDevTools = () => {
    if (!isOpen) {
      setIsOpening(true);
      setTimeout(() => {
        setIsOpen(true);
        setIsOpening(false);
      }, 10);
    } else {
      setIsOpen(false);
    }
  };

  const [openDropdown, setOpenDropdown] = useState(null);

  const hideDropdowns = () => {
    setOpenDropdown(null);
  };

  // State for prediction inputs
  const [modelValue, setModelValue] = useState("hydra-uk-simplybusiness-fqa_0");
  const [scoreValue, setScoreValue] = useState(0.99);

  const copyToClipboard = () => {
    if (domainUserId) {
      navigator.clipboard.writeText(domainUserId).then(() => {
        setCopiedToClipboard(true);
        setTimeout(() => setCopiedToClipboard(false), 2000);
      });
    }
  };

  const sendPrediction = () => {
    setShowPleaseWait(true);
    if (devToolsRef.current) {
      devToolsRef.current.classList.add("wait");
    }

    // TODO: Can we use existing region var for this? We probably won't use UK hydra model in US
    const geoRegion = modelValue.includes("uk") ? "uk" : "us";

    requestIDMRake(
      currentSite,
      geoRegion,
      verticalForIdmRequest,
      journeyId,
      domainUserId,
      scoreValue,
      modelValue,
    );

    // Simulating API call
    setTimeout(() => {
      setShowPleaseWait(false);
      if (devToolsRef.current) {
        devToolsRef.current.classList.remove("wait");
      }
      setIsOpen(false);
    }, 2000);
  };

  const toggleDropdown = (dropdownName) => {
    if (openDropdown === dropdownName) {
      hideDropdowns();
    } else {
      setOpenDropdown(dropdownName);
    }
  };

  // Add appropriate classes based on state
  const getDevToolsClasses = () => {
    let classes = "";
    if (isOpen) classes += " open";
    if (isOpening) classes += " opening";
    if (showPleaseWait) classes += " wait";
    return classes;
  };

  const inlineStyles = {
    devTools: {
      bottom: isOpen ? "130px" : "68px",
      right: "10px",
      transform: isOpen ? "translateY(20px)" : "translateY(0)",
    },
    button: {
      display: isOpen ? "none" : "block",
    },
    tools: {
      display: isOpen || isOpening ? "block" : "none",
      opacity: isOpen ? 1 : 0,
      transform: isOpen ? "scale(1)" : "scale(0.1)",
    },
  };

  const addUserToVariant = (experiment, domainUserId) => {
    fetch(`/dev_tools/amplitude/add_user_to_variant`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        experiment_key: experiment,
        variant_key: "treatment",
        user_ids: [domainUserId],
      }),
    });
    hideDropdowns();
    setIsOpen(false);
  };

  // Styles are relying on indexes of elements being present. Also most a11y rules are not relevant for dev tools
  /* eslint-disable
     react/no-array-index-key,
     jsx-a11y/anchor-is-valid,
     jsx-a11y/click-events-have-key-events,
     jsx-a11y/no-static-element-interactions
  */
  return (
    <div
      id="chopin-dev-tools"
      className={getDevToolsClasses()}
      data-module="dev_tools"
      data-current-site={currentSite}
      tabIndex="-1"
      ref={devToolsRef}
      style={inlineStyles.devTools}
    >
      <button
        onClick={toggleDevTools}
        style={inlineStyles.button}
        type="button"
      >
        Dev tools
      </button>

      <div className="tools" style={inlineStyles.tools} ref={toggleRef}>
        <ul>
          <li>
            <strong>Sites</strong>
            <ul>
              {mainSites.map((site, index) => (
                <li key={`main-site-${index}`}>
                  <a
                    href={site.href}
                    data-site={site.name}
                    className={site.name}
                  >
                    {site.description}
                  </a>
                </li>
              ))}
              <li className="tools__sites">
                <a
                  className={`tools__sites-dropdown${openDropdown === "uk" ? " active" : ""}`}
                  onClick={() => toggleDropdown("uk")}
                >
                  UK Partners
                </a>
                <ul className="tools__site-list">
                  {ukSites.map((site, index) => (
                    <li key={`uk-site-${index}`}>
                      <a
                        href={site.href}
                        data-site={site.name}
                        className={site.name}
                      >
                        {site.description}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="tools__sites">
                <a
                  className={`tools__sites-dropdown${openDropdown === "us" ? " active" : ""}`}
                  onClick={() => toggleDropdown("us")}
                >
                  US Partners
                </a>
                <ul className="tools__site-list">
                  {usSites.map((site, index) => (
                    <li key={`us-site-${index}`}>
                      <a
                        href={site.href}
                        data-site={site.name}
                        className={site.name}
                      >
                        {site.description}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          </li>
          <li>
            <strong>Verticals</strong>
            <ul>
              {Object.entries(verticals).map(([name, href], index) => (
                <li key={`vertical-${index}`}>
                  <a className="vertical" href={href}>
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          </li>
          {domainUserId && (
            <li>
              <strong>Amplitude</strong>
              <ul>
                <li>
                  <a
                    className="tools__amplitude-duid"
                    data-snowplow-domain-userid={domainUserId}
                    onClick={copyToClipboard}
                  >
                    {copiedToClipboard ? "Copied!" : "Copy DUID to Clipboard"}
                  </a>
                </li>
                <li className="tools__sites">
                  <a
                    className={`tools__sites-dropdown${openDropdown === "amplitudeExperiments" ? " active" : ""}`}
                    onClick={() => toggleDropdown("amplitudeExperiments")}
                  >
                    Add DUID to experiment
                  </a>
                  <ul className="tools__site-list">
                    {amplitudeExperiments.map((experiment, index) => (
                      <li key={`experiment-${index}`}>
                        <a
                          className={experiment}
                          onClick={() => addUserToVariant(experiment, domainUserId)}
                        >
                          {experiment}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            </li>
          )}
          {domainUserId && journeyId && currentSite && (
            <li>
              <strong>IDM</strong>
              <ul>
                <li className="tools__sites">
                  <a
                    className={`tools__sites-dropdown${openDropdown === "idm" ? " active" : ""}`}
                    onClick={() => toggleDropdown("idm")}
                  >
                    Trigger prediction in local IDM
                  </a>
                  <ul className="tools__site-list">
                    <div className="tools__prediction-settings">
                      <div id="tools__model_group">
                        <label
                          id="tools__model_label"
                          htmlFor="tools__model_select"
                        >
                          Model
                          <select
                            id="tools__model_select"
                            value={modelValue}
                            onChange={(e) => setModelValue(e.target.value)}
                          >
                            <option value="hydra-uk-simplybusiness-fqa_0">
                              hydra-uk-simplybusiness-fqa_0
                            </option>
                            <option value="hydra-uk-simplybusiness-fqa_1">
                              hydra-uk-simplybusiness-fqa_1
                            </option>
                            <option value="hydra-uk-simplybusiness-fqa_2">
                              hydra-uk-simplybusiness-fqa_2
                            </option>
                            <option value="hydra-uk-simplybusiness-fqa_10">
                              hydra-uk-simplybusiness-fqa_10
                            </option>
                            <option value="hydra-uk-moneysupermarket-fqa_0">
                              hydra-uk-moneysupermarket-fqa_0
                            </option>
                            <option value="hydra-us-simplybusiness-qcp">
                              hydra-us-simplybusiness-qcp
                            </option>
                            <option value="hydra-uk-simplybusiness-qcp">
                              hydra-uk-simplybusiness-qcp
                            </option>
                          </select>
                        </label>
                      </div>
                      <div id="tools__split">
                        <div id="tools__score_group">
                          <label
                            id="tools__score_label"
                            htmlFor="tools__score_input"
                          >
                            Score (0.00-1.00)
                            <input
                              id="tools__score_input"
                              type="number"
                              min="0.00"
                              max="1.00"
                              step="0.01"
                              value={scoreValue}
                              onChange={(e) =>
                                setScoreValue(parseFloat(e.target.value))
                              }
                            />
                          </label>
                        </div>
                        <div
                          id="tools__trigger-prediction"
                          data-journey-id={journeyId}
                          data-snowplow-domain-userid={domainUserId}
                          data-site={currentSite}
                          data-vertical={verticalForIdmRequest}
                          onClick={sendPrediction}
                        >
                          Send
                        </div>
                      </div>
                    </div>
                  </ul>
                </li>
              </ul>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default DevTools;
